// ===================================================================
// HMGP — Reports Page
// Exportable data reports: Pipeline, Project Status, BCA, Funding
// ===================================================================

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, Select, Table, Center, Loader, Alert, SimpleGrid,
} from '@mantine/core';
import { IconDownload, IconReportAnalytics, IconAlertCircle } from '@tabler/icons-react';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { exportToCSV } from '../../../lib/exportUtils';
import {
  HMGP_PROJECT_TYPE_LABELS,
  HMGP_PHASE_LABELS,
  HMGP_PHASE_COLORS,
  HMGP_STATUS_LABELS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType,
  type HMGPPhase,
} from '../../../types/pagrants';

type ReportType = 'pipeline' | 'project_status' | 'bca_summary' | 'funding';

const REPORT_OPTIONS: { value: ReportType; label: string }[] = [
  { value: 'pipeline', label: 'Subapplication Pipeline' },
  { value: 'project_status', label: 'Project Status' },
  { value: 'bca_summary', label: 'BCA Summary' },
  { value: 'funding', label: 'Funding Utilization' },
];

function fmtCurrency(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n) || n === 0) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function num(v: string | number): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

export default function HMGPReports() {
  const { projects, subapplications, fetchProjects, fetchSubapplications } = useHMGPStore();
  const { organizations, fetchOrganizations } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState<ReportType>('pipeline');

  useEffect(() => {
    Promise.all([fetchProjects(), fetchSubapplications(), fetchOrganizations()]).finally(() => setLoading(false));
  }, []);

  // Filter by active disaster
  const filteredProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  const filteredSubapps = useMemo(() => {
    if (!activeDisaster) return [];
    return subapplications.filter(s => String(s.disasterId) === String(activeDisaster.Rid));
  }, [subapplications, activeDisaster]);

  const orgMap = useMemo(() => {
    const m: Record<string, string> = {};
    organizations.forEach(o => { m[String(o.Rid)] = o.orgName; });
    return m;
  }, [organizations]);

  if (loading) return <Center h={200}><Loader /></Center>;

  if (!activeDisaster) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Reports</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="No Active Disaster">
          Select an active incident in the top bar to generate HMGP reports.
        </Alert>
      </Stack>
    );
  }

  // ── Export handlers ─────────────────────────────────────────

  function exportPipeline() {
    const headers = ['Subapp #', 'Title', 'Type', 'Applicant', 'Estimated Cost', 'BCA Ratio', 'Status', 'Priority Rank'];
    const rows = filteredSubapps.map(s => [
      s.subappNumber, s.subappTitle,
      HMGP_PROJECT_TYPE_LABELS[s.projectType as HMGPProjectType] || s.projectType,
      orgMap[s.organizationId] || s.organizationId,
      fmtCurrency(s.estimatedCost), s.bcaRatio || '',
      HMGP_SUBAPP_STATUS_LABELS[s.status as keyof typeof HMGP_SUBAPP_STATUS_LABELS] || s.status,
      s.statePriorityRank || '',
    ]);
    exportToCSV('hmgp-subapplication-pipeline', headers, rows);
  }

  function exportProjectStatus() {
    const headers = ['Project #', 'Title', 'Type', 'Phase', 'Status', 'Estimated Cost', 'Awarded Amount', 'Completion %'];
    const rows = filteredProjects.map(p => [
      p.projectNumber, p.projectTitle,
      HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType,
      HMGP_PHASE_LABELS[p.phase as HMGPPhase] || p.phase,
      HMGP_STATUS_LABELS[p.status as keyof typeof HMGP_STATUS_LABELS] || p.status,
      fmtCurrency(p.estimatedCost), fmtCurrency(p.awardAmount),
      p.performancePeriodEnd ? '' : '', // completion not directly stored; placeholder
    ]);
    exportToCSV('hmgp-project-status', headers, rows);
  }

  function exportBCA() {
    const headers = ['Project #', 'Title', 'Type', 'BCA Ratio', 'PV Benefits', 'PV Costs', 'Useful Life', 'Methodology', 'Cost Effective'];
    const rows = filteredProjects.map(p => [
      p.projectNumber, p.projectTitle,
      HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType,
      p.bcaRatio || '', fmtCurrency(p.pvBenefits), fmtCurrency(p.pvCosts),
      p.usefulLife || '', p.bcaMethodology || '',
      num(p.bcaRatio) >= 1.0 ? 'Yes' : num(p.bcaRatio) > 0 ? 'No' : '',
    ]);
    exportToCSV('hmgp-bca-summary', headers, rows);
  }

  function exportFunding() {
    const headers = ['Project #', 'Title', 'Estimated Cost', 'Awarded', 'Obligated', 'Drawn Down', 'Utilization %'];
    const rows = filteredProjects.map(p => {
      const awarded = num(p.awardAmount);
      const drawn = num(p.drawdownAmount);
      const util = awarded > 0 ? ((drawn / awarded) * 100).toFixed(1) + '%' : '';
      return [
        p.projectNumber, p.projectTitle,
        fmtCurrency(p.estimatedCost), fmtCurrency(p.awardAmount),
        fmtCurrency(p.obligatedAmount), fmtCurrency(p.drawdownAmount), util,
      ];
    });
    exportToCSV('hmgp-funding-utilization', headers, rows);
  }

  const exportFn: Record<ReportType, () => void> = {
    pipeline: exportPipeline,
    project_status: exportProjectStatus,
    bca_summary: exportBCA,
    funding: exportFunding,
  };

  // ── Render tables ─────────────────────────────────────────────

  function renderPipeline() {
    if (filteredSubapps.length === 0) return <Text c="dimmed" ta="center" py="xl">No subapplications found.</Text>;
    return (
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Subapp #</Table.Th>
            <Table.Th>Title</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Applicant</Table.Th>
            <Table.Th ta="right">Est. Cost</Table.Th>
            <Table.Th ta="right">BCR</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th ta="center">Rank</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredSubapps.map(s => (
            <Table.Tr key={s.Rid}>
              <Table.Td>{s.subappNumber}</Table.Td>
              <Table.Td>{s.subappTitle}</Table.Td>
              <Table.Td>{HMGP_PROJECT_TYPE_LABELS[s.projectType as HMGPProjectType] || s.projectType}</Table.Td>
              <Table.Td>{orgMap[s.organizationId] || ''}</Table.Td>
              <Table.Td ta="right">{fmtCurrency(s.estimatedCost)}</Table.Td>
              <Table.Td ta="right">{s.bcaRatio || '-'}</Table.Td>
              <Table.Td>
                <Badge size="xs" variant="light">
                  {HMGP_SUBAPP_STATUS_LABELS[s.status as keyof typeof HMGP_SUBAPP_STATUS_LABELS] || s.status}
                </Badge>
              </Table.Td>
              <Table.Td ta="center">{s.statePriorityRank || '-'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  function renderProjectStatus() {
    if (filteredProjects.length === 0) return <Text c="dimmed" ta="center" py="xl">No projects found.</Text>;
    return (
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Project #</Table.Th>
            <Table.Th>Title</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Phase</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th ta="right">Est. Cost</Table.Th>
            <Table.Th ta="right">Awarded</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredProjects.map(p => (
            <Table.Tr key={p.Rid}>
              <Table.Td>{p.projectNumber}</Table.Td>
              <Table.Td>{p.projectTitle}</Table.Td>
              <Table.Td>{HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType}</Table.Td>
              <Table.Td>
                <Badge size="xs" color={HMGP_PHASE_COLORS[p.phase as HMGPPhase] || 'gray'} variant="light">
                  {HMGP_PHASE_LABELS[p.phase as HMGPPhase] || p.phase}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Badge size="xs" variant="light">
                  {HMGP_STATUS_LABELS[p.status as keyof typeof HMGP_STATUS_LABELS] || p.status}
                </Badge>
              </Table.Td>
              <Table.Td ta="right">{fmtCurrency(p.estimatedCost)}</Table.Td>
              <Table.Td ta="right">{fmtCurrency(p.awardAmount)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  function renderBCA() {
    if (filteredProjects.length === 0) return <Text c="dimmed" ta="center" py="xl">No projects found.</Text>;
    return (
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Project #</Table.Th>
            <Table.Th>Title</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th ta="right">BCR</Table.Th>
            <Table.Th ta="right">PV Benefits</Table.Th>
            <Table.Th ta="right">PV Costs</Table.Th>
            <Table.Th ta="center">Useful Life</Table.Th>
            <Table.Th>Methodology</Table.Th>
            <Table.Th ta="center">Cost Effective</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredProjects.map(p => {
            const bcr = num(p.bcaRatio);
            const hasBCA = bcr > 0;
            return (
              <Table.Tr key={p.Rid}>
                <Table.Td>{p.projectNumber}</Table.Td>
                <Table.Td>{p.projectTitle}</Table.Td>
                <Table.Td>{HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType}</Table.Td>
                <Table.Td ta="right" fw={hasBCA ? 600 : 400} c={hasBCA ? (bcr >= 1 ? 'green' : 'red') : 'dimmed'}>
                  {hasBCA ? bcr.toFixed(2) : '-'}
                </Table.Td>
                <Table.Td ta="right">{num(p.pvBenefits) > 0 ? fmtCurrency(p.pvBenefits) : '-'}</Table.Td>
                <Table.Td ta="right">{num(p.pvCosts) > 0 ? fmtCurrency(p.pvCosts) : '-'}</Table.Td>
                <Table.Td ta="center">{p.usefulLife || '-'}</Table.Td>
                <Table.Td>{p.bcaMethodology || '-'}</Table.Td>
                <Table.Td ta="center">
                  {hasBCA ? (
                    <Badge size="xs" color={bcr >= 1 ? 'green' : 'red'} variant="filled">
                      {bcr >= 1 ? 'Yes' : 'No'}
                    </Badge>
                  ) : (
                    <Badge size="xs" color="gray" variant="light">N/A</Badge>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  }

  function renderFunding() {
    const totalEstimated = filteredProjects.reduce((s, p) => s + num(p.estimatedCost), 0);
    const totalAwarded = filteredProjects.reduce((s, p) => s + num(p.awardAmount), 0);
    const totalObligated = filteredProjects.reduce((s, p) => s + num(p.obligatedAmount), 0);
    const totalDrawn = filteredProjects.reduce((s, p) => s + num(p.drawdownAmount), 0);
    const utilPct = totalAwarded > 0 ? ((totalDrawn / totalAwarded) * 100).toFixed(1) : '0';

    return (
      <Stack gap="md">
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
            <Text size="xs" c="dimmed">Total Estimated</Text>
            <Text size="lg" fw={700}>{fmtCurrency(totalEstimated)}</Text>
          </Paper>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
            <Text size="xs" c="dimmed">Total Awarded</Text>
            <Text size="lg" fw={700}>{fmtCurrency(totalAwarded)}</Text>
          </Paper>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
            <Text size="xs" c="dimmed">Total Obligated</Text>
            <Text size="lg" fw={700}>{fmtCurrency(totalObligated)}</Text>
          </Paper>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
            <Text size="xs" c="dimmed">Drawn Down ({utilPct}%)</Text>
            <Text size="lg" fw={700}>{fmtCurrency(totalDrawn)}</Text>
          </Paper>
        </SimpleGrid>

        {filteredProjects.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">No projects found.</Text>
        ) : (
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Project #</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th ta="right">Estimated</Table.Th>
                <Table.Th ta="right">Awarded</Table.Th>
                <Table.Th ta="right">Obligated</Table.Th>
                <Table.Th ta="right">Drawn</Table.Th>
                <Table.Th ta="right">Utilization</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredProjects.map(p => {
                const awarded = num(p.awardAmount);
                const drawn = num(p.drawdownAmount);
                const util = awarded > 0 ? ((drawn / awarded) * 100).toFixed(1) + '%' : '-';
                return (
                  <Table.Tr key={p.Rid}>
                    <Table.Td>{p.projectNumber}</Table.Td>
                    <Table.Td>{p.projectTitle}</Table.Td>
                    <Table.Td ta="right">{fmtCurrency(p.estimatedCost)}</Table.Td>
                    <Table.Td ta="right">{fmtCurrency(p.awardAmount)}</Table.Td>
                    <Table.Td ta="right">{fmtCurrency(p.obligatedAmount)}</Table.Td>
                    <Table.Td ta="right">{fmtCurrency(p.drawdownAmount)}</Table.Td>
                    <Table.Td ta="right">{util}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    );
  }

  const reportRenderers: Record<ReportType, () => ReactNode> = {
    pipeline: renderPipeline,
    project_status: renderProjectStatus,
    bca_summary: renderBCA,
    funding: renderFunding,
  };

  return (
    <Stack p="md" gap="sm">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Reports</Title>
          <Text size="sm" c="dimmed">Generate and export HMGP data reports</Text>
        </div>
        <Group gap="sm">
          <Select
            value={reportType}
            onChange={v => v && setReportType(v as ReportType)}
            data={REPORT_OPTIONS}
            w={240}
            leftSection={<IconReportAnalytics size={16} />}
          />
          <Button
            leftSection={<IconDownload size={16} />}
            variant="light"
            color="green"
            onClick={() => exportFn[reportType]()}
          >
            Export CSV
          </Button>
        </Group>
      </Group>

      <Paper p="md" withBorder>
        {reportRenderers[reportType]()}
      </Paper>
    </Stack>
  );
}
